import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import { api, errorMessage, getToken } from "@/lib/api";
import {
  workPackageKeys,
  workPackageReviewQueryOptions,
  workPackageVersionsQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { ContractSovWorkspace } from "@/components/contracts/ContractSovWorkspace";
import type {
  ContractFinding,
  ContractProposal,
  ContractWorkPackage,
  ContractWorkPackageStatus,
  WorkPackageVersion,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/contract-work-packages/$packageId")({
  component: ContractReviewWorkspace,
});

type TabName = "summary" | "sov" | "findings" | "evidence" | "history";

function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatMoney(value: string | null | undefined, currency = "USD") {
  const amount = Number(value ?? 0);
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
}

function statusColor(status: ContractWorkPackageStatus) {
  if (status === "completed") return "success" as const;
  if (status === "failed") return "error" as const;
  if (status === "preparing" || status === "executing") return "info" as const;
  if (status === "review_requested") return "warning" as const;
  return "default" as const;
}

function isVersionConflict(error: unknown) {
  return (error as { data?: { error?: { code?: string } } })?.data?.error?.code === "version_conflict";
}

function ContractReviewWorkspace() {
  const { packageId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<TabName>("summary");
  const [headerDraft, setHeaderDraft] = useState<ContractProposal["header"] | null>(null);
  const [correctionReason, setCorrectionReason] = useState<string | null>(null);
  const [contractDetailsOpen, setContractDetailsOpen] = useState(false);

  const review = useQuery({
    ...workPackageReviewQueryOptions(packageId),
    refetchInterval: (state) => {
      const data = state.state.data;
      if (!data?.package) return false;
      return (
        ["preparing", "executing"].includes(data.package.status)
        || ["pending", "processing"].includes(data.execution_request?.status ?? "")
      ) ? 1500 : false;
    },
  });
  const data = review.data;
  const workPackage = data?.package;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: workPackageKeys.review(packageId) });
    await queryClient.invalidateQueries({ queryKey: workPackageKeys.all });
  };

  const action = useMutation({
    mutationFn: async (input: { endpoint: string; body?: Record<string, unknown>; message: string }) => {
      const result = await api<unknown>(input.endpoint, { method: "POST", body: input.body });
      const outcome = result as { status?: string; last_error?: string | null; execution_error?: string | null };
      if (outcome.status === "failed") {
        throw new Error(outcome.last_error || outcome.execution_error || "The operation failed.");
      }
      return { result, message: input.message };
    },
    onSuccess: async ({ message }) => {
      toast.success(message);
      await refresh();
    },
    onError: async (error) => {
      if (isVersionConflict(error)) {
        toast.info("This package changed while it was open. The latest version has been loaded.");
        await refresh();
      } else {
        toast.error(errorMessage(error));
      }
    },
  });

  const saveProposal = useMutation({
    mutationFn: async (proposal: ContractProposal) => {
      if (!workPackage) throw new Error("The package is not loaded.");
      return api<ContractWorkPackage>(`/api/contract-work-packages/${packageId}/draft`, {
        method: "POST",
        body: { version: workPackage.version, proposal, reason: "header_edit" },
      });
    },
    onSuccess: async () => {
      setHeaderDraft(null);
      toast.success("Proposal updated and checks rerun.");
      await refresh();
    },
    onError: async (error) => {
      if (isVersionConflict(error)) {
        setHeaderDraft(null);
        toast.info("This package changed while you were editing. Review the latest values and try again.");
        await refresh();
      } else {
        toast.error(errorMessage(error));
      }
    },
  });

  const findingAction = useMutation({
    mutationFn: ({ finding, verb }: { finding: ContractFinding; verb: "acknowledge" | "resolve" }) =>
      api(`/api/contract-findings/${finding.id}/${verb}`, {
        method: "POST",
        body: { resolution: verb === "resolve" ? "Resolved during contract review" : null },
      }),
    onSuccess: async () => {
      toast.success("Finding updated.");
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const createCorrection = useMutation({
    mutationFn: async (reason: string) => {
      if (!workPackage) throw new Error("The package is not loaded.");
      return api<ContractWorkPackage>(`/api/contract-work-packages/${packageId}/corrections`, {
        method: "POST",
        body: {
          version: workPackage.version,
          reason,
          idempotency_key: `correction:${packageId}:${workPackage.version}`,
        },
      });
    },
    onSuccess: async (created) => {
      setCorrectionReason(null);
      toast.success("Correction package created. The active contract is unchanged until execution.");
      await navigate({
        to: "/contract-work-packages/$packageId",
        params: { packageId: created.id },
      });
    },
    onError: async (error) => {
      if (isVersionConflict(error)) await refresh();
      toast.error(errorMessage(error));
    },
  });

  const openSource = async (pageNumber?: number | null) => {
    if (!data) return;
    const viewer = window.open("", "_blank");
    if (viewer) {
      viewer.opener = null;
      viewer.document.title = "Loading contract document…";
      viewer.document.body.textContent = "Loading contract document…";
    }
    try {
      const token = getToken();
      const response = await fetch(`/api/contracts/${data.contract.id}/document`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`Document unavailable (${response.status})`);
      const objectUrl = URL.createObjectURL(await response.blob());
      const target = `${objectUrl}${pageNumber ? `#page=${pageNumber}` : ""}`;
      if (viewer) viewer.location.replace(target);
      else window.location.assign(target);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      viewer?.close();
      toast.error(errorMessage(error));
    }
  };

  const execute = () => {
    if (!workPackage) return;
    action.mutate({
      endpoint: `/api/contract-work-packages/${packageId}/execute`,
      body: { version: workPackage.version, triggered_actions: ["contract_review_workspace"] },
      message: "Execution completed.",
    });
  };

  const agentRun = workPackage?.execution_decision?.agent_run as { state?: string } | undefined;
  const reviewPhase = (workPackage?.execution_decision?.review as { phase?: string } | undefined)?.phase;
  const isPostExecution = workPackage?.status === "completed" || reviewPhase === "post_execution";
  const canEdit = Boolean(workPackage?.proposal) && !isPostExecution && ![
    "executing", "completed", "review_requested",
  ].includes(workPackage?.status ?? "");

  if (review.isLoading) {
    return <Stack alignItems="center" justifyContent="center" sx={{ height: "calc(100vh - 56px)" }}><CircularProgress /></Stack>;
  }
  if (review.isError || !data || !workPackage) {
    return <Alert severity="error" sx={{ m: 3 }}>Could not load this contract review package.</Alert>;
  }

  const proposal = workPackage.proposal;
  const diagnostic = workPackage.diagnostic_summary;
  return (
    <Box sx={{ minHeight: "calc(100vh - 56px)", bgcolor: "background.default" }}>
      <Paper square elevation={0} sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap" useFlexGap>
          <Button component={Link} to="/contract-work-packages" startIcon={<ArrowBackIcon />} color="inherit">
            Review queue
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" fontWeight={750}>{data.contract.contract_no}</Typography>
              <Chip size="small" label={humanize(workPackage.status)} color={statusColor(workPackage.status)} />
              <Chip size="small" variant="outlined" label={`v${workPackage.version}`} />
            </Stack>
            <Typography color="text.secondary">{data.contract.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {data.project.code} · {data.project.name} · {humanize(workPackage.mode)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {data.contract.source_document_url && (
              <Button variant="outlined" startIcon={<DescriptionOutlinedIcon />} onClick={() => void openSource()}>
                Source document
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={action.isPending || workPackage.status === "executing"}
              onClick={() => action.mutate({
                endpoint: `/api/contract-work-packages/${packageId}/refresh-diagnostics`,
                body: { version: workPackage.version },
                message: "Checks refreshed.",
              })}
            >
              Rerun checks
            </Button>
            {(["prepared", "completed"] as ContractWorkPackageStatus[]).includes(workPackage.status) && (
              <Button
                variant="outlined"
                onClick={() => action.mutate({
                  endpoint: `/api/contract-work-packages/${packageId}/request-review`,
                  body: { version: workPackage.version },
                  message: "Package placed in review.",
                })}
              >Request review</Button>
            )}
            {workPackage.status === "review_requested" && (
              <Button
                variant="contained"
                onClick={() => action.mutate({
                  endpoint: `/api/contract-work-packages/${packageId}/release-review`,
                  body: { version: workPackage.version },
                  message: "Review released.",
                })}
              >Complete review</Button>
            )}
            {(["prepared", "reviewed"] as ContractWorkPackageStatus[]).includes(workPackage.status)
              && !isPostExecution && (
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                disabled={!diagnostic.is_ready || action.isPending}
                onClick={execute}
              >Execute</Button>
            )}
            {workPackage.status === "failed" && (
              <Button
                variant="contained"
                color="error"
                onClick={() => action.mutate({
                  endpoint: `/api/contract-work-packages/${packageId}/${agentRun?.state === "failed" ? "retry-extraction" : "retry-execution"}`,
                  body: agentRun?.state === "failed" ? undefined : { version: workPackage.version },
                  message: agentRun?.state === "failed" ? "Extraction rerun completed." : "Execution retried.",
                })}
              >Retry {agentRun?.state === "failed" ? "extraction" : "execution"}</Button>
            )}
            {isPostExecution && (
              <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setCorrectionReason("")}>
                Start correction
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {workPackage.execution_error && <Alert severity="error" sx={{ mx: { xs: 2, md: 3 }, mt: 2 }}>{workPackage.execution_error}</Alert>}
      {!diagnostic.is_ready && (
        <Alert severity="warning" sx={{ mx: { xs: 2, md: 3 }, mt: 2 }}>
          Still blocked by live contract / SOV math
          {diagnostic.blocking_count
            ? ` (${diagnostic.blocking_count} check${diagnostic.blocking_count === 1 ? "" : "s"})`
            : ""}
          . Acknowledging a finding does not clear this — fix the numbers
          (for example SOV total must equal the original contract amount) before Execute.
        </Alert>
      )}

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
        <Tabs value={tab} onChange={(_, value: TabName) => setTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Contract review sections">
          <Tab value="summary" label="Summary" />
          <Tab value="sov" label={`Schedule of values (${proposal?.sov_items.length ?? 0})`} />
          <Tab value="findings" label={`Findings (${data.findings.filter((item) => item.status !== "resolved").length})`} />
          <Tab value="evidence" label={`Evidence (${data.evidence.length})`} />
          <Tab value="history" label="History" />
        </Tabs>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, pt: 2 }}>
        {tab === "summary" && (
          <SummaryTab
            proposal={proposal}
            workPackage={workPackage}
            canEdit={canEdit}
            onEdit={() => proposal && setHeaderDraft({ ...proposal.header })}
            onOpenContractDetails={() => setContractDetailsOpen(true)}
            onSelectTab={setTab}
          />
        )}
        {tab === "sov" && (
          <ContractSovWorkspace
            workPackage={workPackage}
            evidence={data.evidence}
            canEdit={canEdit}
          />
        )}
        {tab === "findings" && (
          <FindingsTab
            findings={data.findings}
            pending={findingAction.isPending}
            onAction={(finding, verb) => findingAction.mutate({ finding, verb })}
          />
        )}
        {tab === "evidence" && <EvidenceTab evidence={data.evidence} onOpenSource={openSource} />}
        {tab === "history" && <HistoryTab data={data} />}
      </Box>

      <HeaderEditDialog
        value={headerDraft}
        saving={saveProposal.isPending}
        onClose={() => setHeaderDraft(null)}
        onChange={setHeaderDraft}
        onSave={() => proposal && headerDraft && saveProposal.mutate({ ...proposal, header: headerDraft })}
      />
      <ContractDetailsDialog
        header={proposal?.header ?? null}
        open={contractDetailsOpen}
        canEdit={canEdit}
        onClose={() => setContractDetailsOpen(false)}
        onEdit={() => {
          setContractDetailsOpen(false);
          if (proposal) setHeaderDraft({ ...proposal.header });
        }}
      />
      <Dialog open={correctionReason !== null} onClose={() => setCorrectionReason(null)} fullWidth maxWidth="sm">
        <DialogTitle>Start a contract correction</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This creates a versioned proposal. The active contract and SOV do not change until the correction is executed.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Reason for correction"
            value={correctionReason ?? ""}
            onChange={(event) => setCorrectionReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectionReason(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={(correctionReason?.trim().length ?? 0) < 3 || createCorrection.isPending}
            onClick={() => correctionReason && createCorrection.mutate(correctionReason.trim())}
          >Create correction</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SummaryTab({
  proposal,
  workPackage,
  canEdit,
  onEdit,
  onOpenContractDetails,
  onSelectTab,
}: {
  proposal: ContractProposal | null;
  workPackage: ContractWorkPackage;
  canEdit: boolean;
  onEdit: () => void;
  onOpenContractDetails: () => void;
  onSelectTab: (tab: TabName) => void;
}) {
  const header = proposal?.header;
  const diagnostic = workPackage.diagnostic_summary;
  const metrics = [
    {
      label: "Contract amount",
      value: formatMoney(header?.original_contract_amount, header?.currency_code),
      destination: "View schedule of values",
      onClick: () => onSelectTab("sov"),
    },
    {
      label: "SOV total",
      value: formatMoney(String(proposal?.sov_items.reduce((sum, item) => sum + Number(item.original_value), 0) ?? 0), header?.currency_code),
      destination: "View schedule of values",
      onClick: () => onSelectTab("sov"),
    },
    {
      label: "Work retainage",
      value: `${header?.default_work_retainage_percent ?? 0}%`,
      destination: "View contract terms",
      onClick: onOpenContractDetails,
    },
    {
      label: "Payment terms",
      value: header?.payment_terms_days == null ? "Not specified" : `Net ${header.payment_terms_days}`,
      destination: "View contract terms",
      onClick: onOpenContractDetails,
    },
  ];
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Proposed contract</Typography>
        {canEdit && <Button startIcon={<EditOutlinedIcon />} onClick={onEdit}>Edit fields</Button>}
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
        {metrics.map(({ label, value, destination, onClick }) => (
          <Card variant="outlined" key={label}>
            <CardActionArea onClick={onClick} aria-label={`${label}: ${value}. ${destination}.`}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h6" sx={{ mt: 0.5 }}>{value}</Typography>
                <Typography variant="caption" color="primary.main" sx={{ display: "block", mt: 1 }}>
                  {destination}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Readiness checks</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Chip clickable onClick={() => onSelectTab("findings")} label={`${diagnostic.blocking_count} blocking`} color={diagnostic.blocking_count ? "error" : "default"} aria-label={`View ${diagnostic.blocking_count} blocking findings`} />
          <Chip clickable onClick={() => onSelectTab("findings")} label={`${diagnostic.warning_count} warnings`} color={diagnostic.warning_count ? "warning" : "default"} aria-label={`View ${diagnostic.warning_count} warning findings`} />
          <Chip clickable onClick={() => onSelectTab("findings")} label={`${diagnostic.info_count} informational`} aria-label={`View ${diagnostic.info_count} informational findings`} />
          <Chip clickable onClick={() => onSelectTab("history")} label={diagnostic.is_ready ? "Ready to execute" : "Action required"} color={diagnostic.is_ready ? "success" : "error"} aria-label="View execution history" />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Select a check count to view its findings. Select the execution status to view its audit trail.
        </Typography>
        {diagnostic.codes.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{diagnostic.codes.map(humanize).join(" · ")}</Typography>
        )}
      </Paper>
    </Stack>
  );
}

function ContractDetailsDialog({
  header,
  open,
  canEdit,
  onClose,
  onEdit,
}: {
  header: ContractProposal["header"] | null;
  open: boolean;
  canEdit: boolean;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Contract terms</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <SummaryDetail label="Original contract amount" value={formatMoney(header?.original_contract_amount, header?.currency_code)} />
          <SummaryDetail label="Work retainage" value={`${header?.default_work_retainage_percent ?? 0}%`} />
          <SummaryDetail label="Payment terms" value={header?.payment_terms_days == null ? "Not specified" : `Net ${header.payment_terms_days}`} />
          <SummaryDetail label="Billing format" value={humanize(header?.billing_format ?? "not specified")} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {canEdit && <Button variant="contained" onClick={onEdit}>Edit fields</Button>}
      </DialogActions>
    </Dialog>
  );
}

function SummaryDetail({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography>{value}</Typography>
    </Box>
  );
}

function FindingsTab({ findings, pending, onAction }: { findings: ContractFinding[]; pending: boolean; onAction: (finding: ContractFinding, verb: "acknowledge" | "resolve") => void }) {
  const [query, setQuery] = useState("");
  const [severity, setSeverity] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  if (!findings.length) return <Alert severity="success">No findings were recorded for this package.</Alert>;
  const needle = query.trim().toLowerCase();
  const filtered = findings.filter((finding) => (
    (severity === "all" || finding.severity === severity)
    && (!needle || [finding.code, humanize(finding.code), finding.message, finding.field_path, finding.status]
      .some((value) => String(value ?? "").toLowerCase().includes(needle)))
  ));
  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));
  const visible = filtered.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);
  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField
          size="small"
          fullWidth
          label="Search findings"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setPage(0); }}
        />
        <TextField
          size="small"
          select
          label="Severity"
          value={severity}
          onChange={(event) => { setSeverity(event.target.value); setPage(0); }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All severities</MenuItem>
          <MenuItem value="blocking">Blocking</MenuItem>
          <MenuItem value="error">Error</MenuItem>
          <MenuItem value="warning">Warning</MenuItem>
          <MenuItem value="info">Informational</MenuItem>
        </TextField>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Showing {filtered.length.toLocaleString()} of {findings.length.toLocaleString()} findings
      </Typography>
      {!visible.length && <Alert severity="info">No findings match these filters.</Alert>}
      {visible.map((finding) => {
        const isMathBlock =
          finding.severity === "blocking"
          && (finding.code === "SOV_TOTAL_MISMATCH" || finding.code === "CONTRACT_BUDGET_DRIFT");
        return (
        <Paper variant="outlined" sx={{ p: 2 }} key={finding.id}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
            <Stack direction="row" spacing={1} minWidth={220} alignItems="center">
              <Chip size="small" label={humanize(finding.severity)} color={finding.severity === "blocking" || finding.severity === "error" ? "error" : finding.severity === "warning" ? "warning" : "default"} />
              <Typography variant="body2" fontWeight={700}>{humanize(finding.code)}</Typography>
            </Stack>
            <Box sx={{ flex: 1 }}>
              <Typography>{finding.message}</Typography>
              {finding.field_path && <Typography variant="caption" color="text.secondary">Field: {finding.field_path}</Typography>}
              {finding.resolution && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{finding.resolution}</Typography>}
              {isMathBlock && finding.status !== "resolved" && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Fix the SOV / contract amounts to clear Execute. Acknowledge records that you saw this — it does not waive the check.
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1}>
              {finding.status === "open" && (
                <Button size="small" disabled={pending} onClick={() => onAction(finding, "acknowledge")}>
                  Acknowledge
                </Button>
              )}
              {finding.status !== "resolved" && !isMathBlock && (
                <Button size="small" variant="contained" disabled={pending} onClick={() => onAction(finding, "resolve")}>
                  Resolve
                </Button>
              )}
              {finding.status === "resolved" && <Chip size="small" color="success" label="Resolved" />}
              {finding.status === "acknowledged" && isMathBlock && (
                <Chip size="small" label="Acknowledged" />
              )}
            </Stack>
          </Stack>
        </Paper>
        );
      })}
      <TablePagination
        component="div"
        count={filtered.length}
        page={safePage}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Stack>
  );
}

function EvidenceTab({ evidence, onOpenSource }: { evidence: import("@/lib/types").ContractEvidence[]; onOpenSource: (page?: number | null) => Promise<void> }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  if (!evidence.length) return <Alert severity="info">No field-level evidence is attached to this package.</Alert>;
  const needle = query.trim().toLowerCase();
  const filtered = evidence.filter((item) => (
    !needle || [item.field_path, item.source_kind, item.source_excerpt]
      .some((value) => String(value ?? "").toLowerCase().includes(needle))
  ));
  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));
  const visible = filtered.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);
  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        fullWidth
        label="Search evidence"
        value={query}
        onChange={(event) => { setQuery(event.target.value); setPage(0); }}
      />
      <Typography variant="body2" color="text.secondary">
        Showing {filtered.length.toLocaleString()} of {evidence.length.toLocaleString()} evidence records
      </Typography>
      {!visible.length && <Alert severity="info">No evidence matches this search.</Alert>}
      {visible.map((item) => (
        <Paper variant="outlined" sx={{ p: 2 }} key={item.id}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ width: { md: 260 }, flexShrink: 0 }}>
              <Typography fontWeight={700}>{item.field_path}</Typography>
              <Typography variant="caption" color="text.secondary">{humanize(item.source_kind)}{item.confidence ? ` · ${Math.round(Number(item.confidence) * 100)}% confidence` : ""}</Typography>
            </Box>
            <Typography variant="body2" sx={{ flex: 1 }}>{item.source_excerpt || "No source excerpt recorded."}</Typography>
            {(item.page_number || item.source_document_url) && <Button size="small" onClick={() => void onOpenSource(item.page_number)}>Open page {item.page_number ?? ""}</Button>}
          </Stack>
        </Paper>
      ))}
      <TablePagination
        component="div"
        count={filtered.length}
        page={safePage}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Stack>
  );
}

function HistoryTab({ data }: { data: import("@/lib/types").WorkPackageReview }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const versions = useQuery(workPackageVersionsQueryOptions(data.package.id));
  const [preview, setPreview] = useState<WorkPackageVersion | null>(null);
  const loadVersion = useMutation({
    mutationFn: (version: number) => api<WorkPackageVersion>(
      `/api/contract-work-packages/${data.package.id}/versions/${version}`,
    ),
    onSuccess: setPreview,
    onError: (error) => toast.error(errorMessage(error)),
  });
  const restore = useMutation({
    mutationFn: (version: number) => api<ContractWorkPackage>(
      `/api/contract-work-packages/${data.package.id}/versions/${version}/restore`,
      {
        method: "POST",
        body: {
          version: data.package.version,
          reason: `Restore proposal version ${version}`,
        },
      },
    ),
    onSuccess: async () => {
      setPreview(null);
      toast.success("Prior proposal restored as a new version.");
      await queryClient.invalidateQueries({ queryKey: workPackageKeys.review(data.package.id) });
      await queryClient.invalidateQueries({ queryKey: workPackageKeys.versions(data.package.id) });
      await queryClient.invalidateQueries({ queryKey: workPackageKeys.all });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const canRestore = !["executing", "completed", "review_requested"].includes(data.package.status);
  return (
    <Stack spacing={2}>
      <Paper variant="outlined">
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle1" fontWeight={700}>Proposal versions</Typography>
          <Typography variant="body2" color="text.secondary">Every autosave is an immutable snapshot. Restoring creates another version; it never rewrites history.</Typography>
        </Box>
        {versions.isLoading && <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>}
        {versions.data?.map((version) => (
          <Stack key={version.id} direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Chip size="small" label={`v${version.version}`} color={version.version === data.package.version ? "primary" : "default"} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={650}>{humanize(version.reason)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {version.sov_item_count.toLocaleString()} SOV rows · {formatMoney(version.contract_amount)} · {new Date(version.created_at).toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">{version.changed_fields.length} field changes</Typography>
            <Button size="small" disabled={loadVersion.isPending} onClick={() => loadVersion.mutate(version.version)}>Preview</Button>
          </Stack>
        ))}
        {versions.isError && <Alert severity="error">Could not load proposal versions.</Alert>}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Execution</Typography>
        {data.execution_request ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Chip label={humanize(data.execution_request.status)} color={data.execution_request.status === "succeeded" ? "success" : data.execution_request.status === "failed" ? "error" : "info"} />
            <Chip variant="outlined" label={`${data.execution_request.attempt_count} attempt${data.execution_request.attempt_count === 1 ? "" : "s"}`} />
            <Typography variant="body2" color="text.secondary" alignSelf="center">Requested via {humanize(data.execution_request.requested_via)}</Typography>
            {data.execution_request.last_error && <Alert severity="error" sx={{ width: "100%" }}>{data.execution_request.last_error}</Alert>}
          </Stack>
        ) : <Typography color="text.secondary" sx={{ mt: 1 }}>Not executed yet.</Typography>}
      </Paper>
      <Paper variant="outlined">
        {data.events.length ? data.events.map((event, index) => (
          <Box key={event.id} sx={{ p: 2, borderBottom: index < data.events.length - 1 ? 1 : 0, borderColor: "divider" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
              <Typography fontWeight={650}>{event.summary || humanize(event.event_type)}</Typography>
              <Typography variant="caption" color="text.secondary">{new Date(event.occurred_at).toLocaleString()}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">{event.actor || "System"}</Typography>
          </Box>
        )) : <Typography color="text.secondary" sx={{ p: 2 }}>No contract events yet.</Typography>}
      </Paper>
      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="md">
        <DialogTitle>Proposal version {preview?.version}</DialogTitle>
        <DialogContent>
          {preview && <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">This snapshot has {preview.sov_item_count.toLocaleString()} SOV rows totaling {formatMoney(preview.contract_amount, preview.proposal.header.currency_code)}.</Alert>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
              <Typography variant="body2"><strong>Contract:</strong> {preview.proposal.header.contract_no}</Typography>
              <Typography variant="body2"><strong>Mode:</strong> {humanize(preview.mode)}</Typography>
              <Typography variant="body2"><strong>Title:</strong> {preview.proposal.header.title}</Typography>
              <Typography variant="body2"><strong>Saved:</strong> {new Date(preview.created_at).toLocaleString()}</Typography>
            </Box>
            <Divider />
            <Typography variant="subtitle2">Changed fields</Typography>
            <Typography variant="body2" color="text.secondary">{preview.changed_fields.length ? preview.changed_fields.slice(0, 30).join(" · ") : "No field-level changes recorded."}</Typography>
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto" }}>
              {preview.proposal.sov_items.slice(0, 100).map((item) => <Stack key={`${item.object_key}:${item.item_code}`} direction="row" spacing={2} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
                <Typography width={90}>{item.item_code}</Typography><Typography flex={1} noWrap>{item.description}</Typography><Typography>{formatMoney(item.original_value, preview.proposal.header.currency_code)}</Typography>
              </Stack>)}
            </Paper>
          </Stack>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
          {preview && canRestore && preview.version !== data.package.version && <Button variant="contained" color="warning" disabled={restore.isPending} onClick={() => restore.mutate(preview.version)}>Restore as new version</Button>}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function HeaderEditDialog({ value, saving, onClose, onChange, onSave }: { value: ContractProposal["header"] | null; saving: boolean; onClose: () => void; onChange: (value: ContractProposal["header"] | null) => void; onSave: () => void }) {
  if (!value) return null;
  const set = (field: keyof ContractProposal["header"], next: string | number | null) => onChange({ ...value, [field]: next });
  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>Edit proposed contract</DialogTitle>
      <DialogContent sx={{ pt: "16px !important" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
          <TextField label="Contract number" value={value.contract_no} onChange={(event) => set("contract_no", event.target.value)} />
          <TextField label="Currency" value={value.currency_code} inputProps={{ maxLength: 3 }} onChange={(event) => set("currency_code", event.target.value.toUpperCase())} />
          <TextField label="Title" value={value.title} onChange={(event) => set("title", event.target.value)} sx={{ gridColumn: { sm: "1 / -1" } }} />
          <TextField label="Original contract amount" type="number" value={value.original_contract_amount} onChange={(event) => set("original_contract_amount", event.target.value)} />
          <TextField label="Payment terms (days)" type="number" value={value.payment_terms_days ?? ""} onChange={(event) => set("payment_terms_days", event.target.value === "" ? null : Number(event.target.value))} />
          <TextField label="Work retainage %" type="number" value={value.default_work_retainage_percent} onChange={(event) => set("default_work_retainage_percent", event.target.value)} />
          <TextField label="Material retainage %" type="number" value={value.default_material_retainage_percent} onChange={(event) => set("default_material_retainage_percent", event.target.value)} />
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={saving || !value.contract_no.trim() || !value.title.trim()} onClick={onSave}>Save and rerun checks</Button></DialogActions>
    </Dialog>
  );
}
